import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";
import { errorMessage, isBelowCost } from "~/lib/api";
import { formatCount, formatDateTime } from "~/lib/format";
import {
  useBackfillFeeSchedules,
  type BackfillFeeSchedulesResult,
  type FeeRateInput,
  type FeeScheduleGap,
  type PaymentMethod,
} from "~/lib/queries";
import { jakartaDate, todayInJakarta, wibMidnight } from "./-fee-format";
import { EMPTY_RATE, RateFields, toRateInput, type RateDraft } from "./-rate-fields";

/**
 * Fill every gap at once (PRO-1202): one start date, one rate per method, one
 * window per merchant x method that would otherwise refuse its next
 * settlement.
 *
 * Defaults to pass-through -- the merchant pays exactly what DurianPay charges
 * Kugie, zero margin -- because that is what every merchant was effectively
 * paying before the ledger existed. Anything else is a commercial decision and
 * belongs on the merchant's own page.
 */
export function BackfillDialog({
  open,
  onOpenChange,
  gaps,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gaps: FeeScheduleGap[];
}) {
  const methods = useMemo(
    () => Array.from(new Set(gaps.map((g) => g.payment_method))),
    [gaps],
  );
  // The latest start that still covers every waiting payment.
  const earliest = useMemo(() => {
    const dates = gaps.flatMap((g) => (g.oldest_unsettled_at ? [jakartaDate(g.oldest_unsettled_at)] : []));
    return dates.sort()[0] ?? todayInJakarta();
  }, [gaps]);

  const [from, setFrom] = useState(earliest);
  const [passThrough, setPassThrough] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, RateDraft>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<BackfillFeeSchedulesResult | null>(null);
  const backfill = useBackfillFeeSchedules();

  const draftFor = (m: PaymentMethod) => drafts[m] ?? EMPTY_RATE;

  function submit() {
    setFormError(null);
    const rates: FeeRateInput[] = [];
    for (const method of methods) {
      const draft = draftFor(method);
      const effective = passThrough
        ? { ...draft, percent: draft.costPercent, fixed: draft.costFixed }
        : draft;
      const parsed = toRateInput(method, effective);
      if ("error" in parsed) {
        setFormError(parsed.error);
        return;
      }
      rates.push(parsed.rate);
    }
    backfill.mutate(
      { effective_from: wibMidnight(from), rates },
      {
        onSuccess: (res) => {
          setResult(res);
          toast.success(`${res.created.length} fee schedule${res.created.length === 1 ? "" : "s"} created.`);
        },
      },
    );
  }

  function close(next: boolean) {
    if (backfill.isPending && !next) return;
    if (!next) {
      setResult(null);
      backfill.reset();
    }
    onOpenChange(next);
  }

  const startsLate = from > earliest;

  return (
    <Dialog open={open} onOpenChange={close} disablePointerDismissal>
      <DialogContent showClose={!backfill.isPending} className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Backfill missing fee schedules</DialogTitle>
          <DialogDescription>
            Opens one window per merchant and method below, in one go. Each one is audited like a
            schedule entered by hand. Running it again creates nothing new.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <BackfillResult result={result} />
        ) : (
          <div className="space-y-4 text-sm">
            <GapList gaps={gaps} />

            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="backfill-from" className="text-xs text-muted-foreground">
                  Start (midnight Jakarta)
                </Label>
                <Input
                  id="backfill-from"
                  type="date"
                  value={from}
                  max={todayInJakarta()}
                  onChange={(e) => setFrom(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="flex items-center gap-2 pb-2">
                <Switch id="backfill-pass" checked={passThrough} onCheckedChange={(next) => setPassThrough(next)} />
                <Label htmlFor="backfill-pass">Pass-through: merchant pays DurianPay's rate (zero margin)</Label>
              </div>
            </div>
            {startsLate ? (
              <p className="rounded-md border border-warning/30 bg-warning/10 p-2.5 text-xs">
                A payment has been waiting to settle since {earliest}. A window starting later will not
                cover it, and its settlement will still be refused.
              </p>
            ) : null}

            {methods.map((method) => (
              <div key={method} className="space-y-2 rounded-md border p-3">
                <p className="font-medium">{method}</p>
                {passThrough ? (
                  <PassThroughFields
                    idPrefix={`backfill-${method}`}
                    draft={draftFor(method)}
                    onChange={(next) => setDrafts({ ...drafts, [method]: next })}
                  />
                ) : (
                  <RateFields
                    idPrefix={`backfill-${method}`}
                    draft={draftFor(method)}
                    onChange={(next) => setDrafts({ ...drafts, [method]: next })}
                  />
                )}
              </div>
            ))}

            <p className="text-xs text-muted-foreground">
              Take DurianPay's rates from the contract. A blank field counts as 0.
            </p>

            {formError ? <p className="text-xs text-destructive">{formError}</p> : null}
            {backfill.error ? (
              <p className="text-xs text-destructive">
                {isBelowCost(backfill.error)
                  ? `${errorMessage(backfill.error)} Nothing was saved. Below-cost rates need an override, which is set one merchant at a time on the merchant's page.`
                  : `${errorMessage(backfill.error)} Nothing was saved.`}
              </p>
            ) : null}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button size="sm" onClick={() => close(false)}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="ghost" size="sm" disabled={backfill.isPending} onClick={() => close(false)}>
                Cancel
              </Button>
              <Button size="sm" disabled={backfill.isPending || methods.length === 0} onClick={submit}>
                {backfill.isPending ? "Working…" : `Create ${gaps.length} schedule${gaps.length === 1 ? "" : "s"}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Only DurianPay's two numbers; the merchant side is copied from them. */
function PassThroughFields({
  idPrefix,
  draft,
  onChange,
}: {
  idPrefix: string;
  draft: RateDraft;
  onChange: (next: RateDraft) => void;
}) {
  return (
    <div className="flex items-end gap-2">
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-cost-pct`} className="text-xs text-muted-foreground">
          DurianPay %
        </Label>
        <Input
          id={`${idPrefix}-cost-pct`}
          inputMode="decimal"
          value={draft.costPercent}
          placeholder="0.7"
          onChange={(e) => onChange({ ...draft, costPercent: e.target.value })}
          className="w-28"
        />
      </div>
      <span className="pb-2 text-muted-foreground">+</span>
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-cost-fixed`} className="text-xs text-muted-foreground">
          Flat Rp
        </Label>
        <Input
          id={`${idPrefix}-cost-fixed`}
          inputMode="decimal"
          value={draft.costFixed}
          placeholder="0"
          onChange={(e) => onChange({ ...draft, costFixed: e.target.value })}
          className="w-28"
        />
      </div>
    </div>
  );
}

function GapList({ gaps }: { gaps: FeeScheduleGap[] }) {
  return (
    <ul className="max-h-48 divide-y overflow-y-auto rounded-md border text-xs">
      {gaps.map((gap) => (
        <li key={`${gap.merchant_id}-${gap.payment_method}`} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
          <span>
            <Link to="/merchants/$merchantId" params={{ merchantId: gap.merchant_id }} className="font-medium text-primary hover:underline">
              {gap.merchant_name}
            </Link>{" "}
            <span className="text-muted-foreground">· {gap.payment_method}</span>
          </span>
          <span className="text-muted-foreground">
            {gap.unsettled_count > 0
              ? `${formatCount(gap.unsettled_count)} waiting to settle, oldest ${formatDateTime(gap.oldest_unsettled_at)}`
              : `${formatCount(gap.transaction_count)} settled, none waiting`}
            {gap.in_force_now ? " · current window starts too late" : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

function BackfillResult({ result }: { result: BackfillFeeSchedulesResult }) {
  return (
    <div className="space-y-3 text-sm">
      <p>
        <strong className="font-medium">{result.created.length}</strong> created,{" "}
        <strong className="font-medium">{result.skipped.length}</strong> skipped.
      </p>
      {result.skipped.length ? (
        <ul className="divide-y rounded-md border text-xs">
          {result.skipped.map((skip) => (
            <li key={`${skip.merchant_id}-${skip.payment_method}`} className="px-3 py-2">
              <span className="font-medium">{skip.merchant_name}</span> · {skip.payment_method}:{" "}
              <span className="text-muted-foreground">{skip.reason}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {result.remaining_gaps.length ? (
        <p className="rounded-md border border-warning/30 bg-warning/10 p-2.5 text-xs">
          {result.remaining_gaps.length} merchant/method pair{result.remaining_gaps.length === 1 ? "" : "s"} still
          {result.remaining_gaps.length === 1 ? " has" : " have"} no schedule covering every waiting payment. Their
          settlements will keep failing until that is fixed.
        </p>
      ) : (
        <p className="rounded-md border border-success/25 bg-success/10 p-2.5 text-xs">
          Every merchant and method in use can now be priced. Failed settlements can be retried from
          Webhook Events.
        </p>
      )}
    </div>
  );
}
